import saveAs from "file-saver";
import { TFunction } from "i18next";
import XLSX from 'xlsx-js-style';
import { Shipment } from "@/types/shipment";
import { BoxItem, PackBox, PackOrder } from "@/types/packing";
import { PurchaseOrderItem, SupplierPrice } from "@/types/purchase";
import { divided, multi, noRateAmount, plus } from "@/utils/calc";
import { DictItem, DictLike } from "@/types/dict";
import { orderFetch } from "@/apis/inventoryv2";
import { OrderItemV2 } from "@/types/inventoryV2";

export const getHeaders = (t: TFunction) => [
  t('product.code'),
  t('counting.count'),
  t('shipment.unitPrice'),
];
export const generateTemplate = (t: TFunction, name?: string) => {
  // 获取表头
  const headers = getHeaders(t);

  // 工作表数据（仅表头）
  const worksheetData = [headers];

  // 创建工作表
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  // 设置列宽
  worksheet['!cols'] = [
    { wch: 20 },  // 编码
    { wch: 15 },  // 数量
    { wch: 15 },  // 单价
  ];

  // 创建工作簿
  const workbook = XLSX.utils.book_new();
  const sheetName = name || t('requirement.inputTemp');
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  // 生成Excel文件并下载
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const dataBlob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
  });

  const fileName = `${name || t('requirement.inputTemp')}.xlsx`;
  saveAs(dataBlob, fileName);
};
/**
 * 获取单元格值并trim
 */
const getCellValue = (row: any[], index: number): string => {
  if (row[index] === undefined || row[index] === null) return '';
  return String(row[index]).trim();
};

export interface ImportProduct {
  productCode: string;
  count: number;
  unitPrice: number;
}
//解析表格
export const parseData = (file: File, t: TFunction): Promise<ImportProduct[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // 解析为二维数组
        const jsonData = XLSX.utils.sheet_to_json<string[]>(worksheet, { header: 1 });

        if (!jsonData || jsonData.length === 0) {
          reject(new Error('invalid'));
          return;
        }

        // 验证表头
        const headers = jsonData[0];
        const expectedHeaders = getHeaders(t);
        const isValidHeader = expectedHeaders.every((header, index) => header === headers[index]);
        if (!isValidHeader) {
          reject(new Error('invalid'));
          return;
        }

        // 解析数据行（跳过表头）
        const result: ImportProduct[] = [];
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (!row || row.length === 0) continue;
          let code = getCellValue(row, 0);
          let count = getCellValue(row, 1);
          let unitPrice = getCellValue(row, 2);

          result.push({
            productCode: code,
            count: Number(count || ''),
            unitPrice: Number(unitPrice || '')
          });
        }

        resolve(result.filter(item => !!item.productCode && !!item.count));
      } catch {
        reject(new Error('invalid'));
      }
    };
    reader.onerror = () => reject(new Error('invalid'));
    reader.readAsArrayBuffer(file);
  });
};

const borderAndCenter = (sheet: XLSX.WorkSheet, fontName: string = '宋体', fontSize: number = 12) => {
  const style = {
    font: {
      name: fontName,
      sz: fontSize,
    },
    border: {
      top: { style: 'thin', color: { rgb: '000000' } },
      bottom: { style: 'thin', color: { rgb: '000000' } },
      left: { style: 'thin', color: { rgb: '000000' } },
      right: { style: 'thin', color: { rgb: '000000' } },
    },
    alignment: {
      horizontal: 'center',
      vertical: 'center',
      wrapText: true,
    },
  };

  const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1');
  for (let r = range.s.r; r <= range.e.r; r++) {
    for (let c = range.s.c; c <= range.e.c; c++) {
      const cellAddress = XLSX.utils.encode_cell({ r, c });
      if (!sheet[cellAddress]) continue;
      sheet[cellAddress].s = { ...sheet[cellAddress].s, ...style };
    }
  }
};



export type ProIdPriceRec = Record<string, SupplierPrice>;
export const exportShipment = async (shipments: Shipment[], packOrders: PackOrder[], currencys: DictLike[], exchangeRate: DictItem[]) => {
  const getExchangeVal = (cur1: string, cur2: string) => {
    const k1 = currencys.find(c => c.desc === cur1)?.key;
    const k2 = currencys.find(c => c.desc === cur2)?.key;
    if (k1 && k2) {
      const c1 = exchangeRate.find(c => c.key === k1);
      const c2 = exchangeRate.find(c => c.key === k2);
      if (c1?.value && c2?.value) {
        return divided(c1.value, c2.value)
      }
    }
    return '1';
  };

  const getlotKey = (porductId: string, lot: string) => `${porductId}<->${lot}`;

  const workbook = XLSX.utils.book_new();

  let boxIds: string[] = [];
  let shipStr: string = '';
  shipments.forEach((shipment, index) => {
    shipment.boxes?.forEach(b => boxIds.push(b.id));
    if (index > 0) shipStr += '/';
    shipStr += `${shipment.number}`;
  })
  let sourceBoxes: Array<PackBox & { remained: boolean }> = [];
  let outOrderIds: string[] = [];
  packOrders.forEach(packOrder => {
    packOrder?.inventoryOrders?.forEach(order => {
      if (!outOrderIds.includes(order.id)) outOrderIds.push(order.id)
    })
    let sourceB = (packOrder.boxes?.filter(b => boxIds.includes(b.id)) || [])
      .sort((a, b) => a.createdAt - b.createdAt)
      .map(b => ({ ...b, remained: false }));
    if (sourceB?.length) sourceBoxes.push(...sourceB);
  })

  //PL
  const plData: [string, string, string, string, string, string, string, string,][] = [
    ['序号', 'DESCRIPTION', '', `QUANTITY\n(PCS)`, `CTNS`, `NW\n(KG)`, `GW\n(KG)`, '料号',]
  ];
  const plTotal: { qty: number; cnt: number; nw: number; gw: number } = { qty: 0, cnt: 0, nw: 0, gw: 0 };
  //CI
  const ciData: [string, string, string, string, string, string, string, string, string, string, string][] = [
    ['序号', 'DESCRIPTION', '', `QUANTITY\n(PCS/KG)`, `Unit Price(USD)`, `Amount\n(US $)`, '料号', '', '含税单价', '结算币种', 'RMB金额']
  ];
  const ciTotal: { qty: number; amt: number } = { qty: 0, amt: 0 };
  //申报要素数据
  const declareData: [string, string, string, string, string, string, string, string, string, string, string, string, string][] = [
    ['款号', '描述', 'HS编码', '品名', '型号', '品牌', '产地', '其它要素', '品牌类型', '享惠情况', '料号', '料号品名', '数量'],
  ];
  let tempData: Array<[
    index: number,        //0
    name: string,         //1
    model: string,        //2
    count: number,        //3
    ctns: number,         //4
    nw: number,           //5
    gw: number,           //6
    code: string,         //7
    boxNumber: string,    //8
    productId: string,    //9
    price: number,        //10
    amount: number,       //11
    place: string,        //12
    brand: string,        //13
    brandType: string,    //14
    hsCode: string,        //15
    purCurrency: string,     //16
    purPrice: string,        //17
    purPriceAmt: string,        //18
  ]> = [];
  let lots: string[] = [];
  sourceBoxes.forEach(b => b.items?.forEach(i => lots.push(i.lot)));
  let purLotMap = new Map<string, PurchaseOrderItem>();
  let orderItems: OrderItemV2[] = [];

  if (outOrderIds.length) {
    for (let ooi = 0; ooi < outOrderIds.length; ooi++) {
      let outOrder = await orderFetch(outOrderIds[ooi]);
      orderItems.push(...(outOrder.items || []));
    }
  };
  orderItems.forEach(oi => {
    let key = getlotKey(oi.productId, oi.lot);
    if (!!!purLotMap.get(key) && oi.lotItem?.purchaseItem) purLotMap.set(key, oi.lotItem.purchaseItem);
  });

  let idx: number = 0;
  sourceBoxes.forEach((b, bidx) => {
    b.items.forEach(item => {
      let lotKey = getlotKey(item.productId, item.lot);
      let putItem = purLotMap.get(lotKey);
      let purPriceObj: { priceStr: string, taxRate: string, currency: string } = {
        priceStr: putItem?.priceStr ?? '',
        taxRate: putItem?.taxRate ?? '',
        currency: putItem?.order?.currency ?? '',
      };
      let purCurrency = currencys.find(c => c.key === purPriceObj?.currency)?.desc ?? '';
      let purPrice = purPriceObj?.priceStr ?? '0';
      let purTax = purPriceObj?.taxRate ?? purPriceObj?.taxRate ?? '0';
      let exchangeVal = getExchangeVal(purCurrency, "USD");
      let noRatePrice = noRateAmount(purPrice, purTax);
      let price = Number(multi(noRatePrice, exchangeVal));
      let amount = Number(multi(price + '', item.count + ''));
      let purPriceAmt = Number(multi(purPrice + '', item.count + '')).toFixed(2);
      // let price = shipment.items.find(i => i.productId === item.productId)?.unitPrice ?? 0;
      // let amount = Number(multi(String(price), String(item.count)));

      // console.log(`索引:>>>${idx + 1},箱子:>>>${b.boxNumber},料号:>>>${item.product.code},品牌:>>>${item.brand},数量:>>>${item.count},净重:>>>${item.netWeight},批次号:>>>${item.lot},币种:>>>${purCurrency},采购价:>>>${purPrice},RMB:>>>${purPriceAmt},`);
      if (b.remained) {
        //品牌料号相同，箱子被占用，找到占用箱子的条目
        let findRemainIdx = tempData.findIndex(data => data[8].split(',,').includes(b.boxNumber));
        // 占用箱子的条目和当前是同一商品：相同箱子&&相同商品，原纪录直接累计count、netweight
        if (tempData[findRemainIdx][9] === item.productId && tempData[findRemainIdx][13] === (item.brand || '')) {
          tempData[findRemainIdx][3] = Number(plus(String(tempData[findRemainIdx][3]), String(item.count)));
          tempData[findRemainIdx][5] = Number(plus(String(tempData[findRemainIdx][5] || 0), String(item.netWeight || 0)));
          tempData[findRemainIdx][11] = Number(plus(String(tempData[findRemainIdx][11]), String(amount)));
          tempData[findRemainIdx][18] = plus(tempData[findRemainIdx][18], String(purPriceAmt));
        } else {
          //找一下之前有没有已经push过的不占用箱子且相同物料，如果有，累计数量相关
          let noBoxItemIdx = tempData.findIndex(data => data[9] === item.productId && data[13] === (item.brand || '') && !!!data[4]);
          if (noBoxItemIdx > -1) {
            tempData[noBoxItemIdx][3] = Number(plus(String(tempData[noBoxItemIdx][3]), String(item.count)));
            tempData[noBoxItemIdx][5] = Number(plus(String(tempData[noBoxItemIdx][5] || 0), String(item.netWeight || 0)));
            tempData[noBoxItemIdx][11] = Number(plus(String(tempData[noBoxItemIdx][11]), String(amount)));
            tempData[noBoxItemIdx][18] = plus(tempData[noBoxItemIdx][18], String(purPriceAmt));
          } else {
            // 占用箱子的条目和当前不是同一商品：新纪录不占用箱子
            tempData.push([
              ++idx,
              item?.product?.name ?? '',
              item?.product?.model ?? '',
              item.count,
              0,
              item.netWeight || 0,
              0,
              item?.product?.code ?? '',
              '',
              item?.productId ?? '',
              price,
              amount,
              item?.originPlace ?? '',
              item?.brand ?? '',
              item?.brandType ?? '',
              item?.hsCode ?? item?.product?.hsCode ?? '',
              purCurrency,
              purPrice,
              purPriceAmt
            ]);
          }
        }
      } else {
        let findIdx = tempData.findIndex(data => data[9] === item.productId && data[13] === (item.brand || ''));
        if (findIdx > -1) {
          //品牌料号相同，箱子没被占用，原纪录累计箱子数量，累计毛重，累计净重，无新纪录
          tempData[findIdx][4] += 1;
          tempData[findIdx][8] = tempData[findIdx][8] + `,,${b.boxNumber}`;
          tempData[findIdx][6] = Number(plus(String(tempData[findIdx][6] || 0), String(b.grossWeightG || 0)));
          tempData[findIdx][5] = Number(plus(String(tempData[findIdx][5] || 0), String(item.netWeight || 0)));
          tempData[findIdx][3] = Number(plus(String(tempData[findIdx][3]), String(item.count)));
          tempData[findIdx][11] = Number(plus(String(tempData[findIdx][11]), String(amount)));
          tempData[findIdx][18] = plus(tempData[findIdx][18], String(purPriceAmt));
        } else {
          //品牌料号不同，箱子没被占用，新纪录
          tempData.push([
            ++idx,
            item?.product?.name ?? '',
            item?.product?.model ?? '',
            item.count,
            1,
            item.netWeight || 0,
            b.grossWeightG || 0,
            item?.product?.code ?? '',
            b.boxNumber,
            item?.productId ?? '',
            price,
            amount,
            item?.originPlace ?? '',
            item?.brand ?? '',
            item?.brandType ?? '',
            item?.hsCode ?? item?.product?.hsCode ?? '',
            purCurrency,
            purPrice,
            purPriceAmt
          ]);
        }
        sourceBoxes[bidx].remained = true;
      }
    })
  });
  tempData.forEach(data => {
    // PL
    plTotal.qty = Number(plus(String(plTotal.qty), String(data[3])));
    plTotal.cnt = Number(plus(String(plTotal.cnt), String(data[4])));
    plTotal.nw = Number(plus(String(plTotal.nw), String(data[5])));
    plTotal.gw = Number(plus(String(plTotal.gw), String(data[6])));
    plData.push([
      data[0] + '',
      data[1] + '',
      data[2] + '',
      data[3] + '',
      data[4] + '',
      data[5] + '',
      data[6] + '',
      data[7] + '',
    ]);
    //CI
    ciTotal.qty = Number(plus(String(ciTotal.qty), String(data[3])));
    ciTotal.amt = Number(plus(String(ciTotal.amt), String(data[11])));
    ciData.push([
      data[0] + '',
      data[1] + '',
      data[2] + '',
      data[3] + '',
      '',//data[10] + '',
      '0.00',//data[11] + '',
      data[7] + '',
      '',
      data[17],
      data[16],
      data[18]
    ]);
    //申报要素
    declareData.push([
      data[0] + '',
      data[2] + '',
      data[15] + '',
      data[1] + '',
      '',
      data[13] + '',
      data[12] + '',
      '',
      data[14] + '',
      '',
      data[7] + '',
      data[1] + '',
      data[3] + '',
    ])
  });
  plData.push([
    '',
    '',
    '合计',
    plTotal.qty + '',
    plTotal.cnt + '',
    plTotal.nw + '',
    plTotal.gw + '',
    ''
  ]);

  ciData.push([
    '',
    '',
    '合计',
    ciTotal.qty + '',
    '',
    '0.00',//ciTotal.amt + '',
    '',
    '',
    '',
    '',
    '',
  ]);

  const plSheet = XLSX.utils.aoa_to_sheet([...plData]);
  const clSheet = XLSX.utils.aoa_to_sheet([...ciData]);
  const declareSheet = XLSX.utils.aoa_to_sheet([...declareData]);

  // 宽高
  plSheet['!cols'] = [
    { wch: 10 },
    { wch: 40 },
    { wch: 40 },
    { wch: 30 },
    { wch: 30 },
    { wch: 30 },
    { wch: 30 },
    { wch: 30 },
  ];
  plSheet['!rows'] = [
    { hpt: 44 }
  ];

  clSheet['!cols'] = [
    { wch: 10 },
    { wch: 40 },
    { wch: 40 },
    { wch: 30 },
    { wch: 30 },
    { wch: 30 },
    { wch: 30 },
    { wch: 20 },
    { wch: 30 },
    { wch: 30 },
    { wch: 30 },
  ];
  clSheet['!rows'] = [
    { hpt: 44 }
  ];

  declareSheet['!cols'] = [
    { wch: 10 },
    { wch: 40 },
    { wch: 40 },
    { wch: 20 },
    { wch: 40 },
    { wch: 40 },
    { wch: 20 },
    { wch: 30 },
    { wch: 30 },
    { wch: 20 },
    { wch: 20 },
    { wch: 20 },
  ];
  declareSheet['!rows'] = [
    { hpt: 44 }
  ];

  //合并单元格
  plSheet['!merges'] = [
    { s: { r: 0, c: 1 }, e: { r: 0, c: 2 } }  // B1 到 C1
  ];
  clSheet['!merges'] = [
    { s: { r: 0, c: 1 }, e: { r: 0, c: 2 } }  // B1 到 C1
  ];

  //样式：
  borderAndCenter(plSheet);
  borderAndCenter(clSheet);
  borderAndCenter(declareSheet);

  XLSX.utils.book_append_sheet(workbook, plSheet, 'PL');
  XLSX.utils.book_append_sheet(workbook, clSheet, 'CI');
  XLSX.utils.book_append_sheet(workbook, declareSheet, '申报要素');

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const dataBlob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
  });
  saveAs(dataBlob, `${shipStr}.xlsx`);
};